import { Link } from "react-router-dom";
import { ArrowRight, FileCheck2, CalendarClock, ClipboardList, Building2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CheckList from "@/components/CheckList";
import CtaBand from "@/components/CtaBand";
import { commercialServices, commercialClients, images, business } from "@/data/site";

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Commercial Roofing",
  provider: { "@type": "RoofingContractor", name: business.name, telephone: "+1-215-806-5038" },
  areaServed: "Bucks County, Pennsylvania",
};

const highlights = [
  { icon: FileCheck2, title: "Written Estimates & Reports", body: "Detailed written estimates and inspection reports are available for your records and decision-making." },
  { icon: ClipboardList, title: "Maintenance Plans", body: "Ongoing maintenance plans are available to extend the life of your commercial roof system." },
  { icon: Building2, title: "Free Commercial Inspections", body: "We offer free commercial roof inspections to assess condition and recommend next steps." },
  { icon: CalendarClock, title: "After-Hours Availability", body: "After-hours and weekend commercial work may be available to minimize disruption to your business." },
];

export default function Commercial() {
  return (
    <>
      <Seo
        title="Commercial Roofing in Bucks County, PA | Future Roofing and Repair"
        description="Commercial roofing in Bucks County, PA: flat roofing, TPO, EPDM, PVC, built-up & metal roofing, coatings, inspections, maintenance and repairs. Call 215-806-5038."
        keywords="Commercial roofing Bucks County, Flat roofing contractor, TPO roofing, EPDM roofing, PVC roofing, Warehouse roofing, Commercial roof repair"
        schema={schema}
      />
      <PageHero
        kicker="Commercial Roofing"
        title="Commercial Roofing You Can Count On"
        subtitle="Flat and low-slope roof systems, coatings, inspections, and maintenance for businesses and property owners across Bucks County."
        image={images.commercial}
      />

      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionHeading kicker="Who We Work With" title="Partners in Property" />
            <p className="text-charcoal/80 leading-relaxed mb-6">
              Future Roofing and Repair works with property managers, landlords, builders, contractors,
              developers, and a wide range of commercial properties:
            </p>
            <CheckList items={commercialClients} columns={2} />
          </div>
          <div className="bg-cream border border-charcoal/20 shadow-md p-8">
            <SectionHeading kicker="Full Service List" title="Commercial Services" />
            <CheckList items={commercialServices} columns={2} />
          </div>
        </div>
      </section>

      <section className="bg-cream-200 border-y border-charcoal/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <SectionHeading
            kicker="Built for Business"
            title="What Sets Our Commercial Service Apart"
            center
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {highlights.map((h) => (
              <div key={h.title} className="bg-cream border border-charcoal/20 shadow-md p-6">
                <h.icon className="w-9 h-9 text-rust mb-4" />
                <h3 className="font-heading text-xl uppercase text-charcoal leading-tight">{h.title}</h3>
                <p className="mt-2 text-sm text-charcoal/75 leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        variant="default"
        kicker="Commercial Quote"
        title="Request a Commercial Roofing Quote"
        text="Get a written estimate or schedule a free commercial inspection."
      />

      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <Link
            to="/contact"
            data-testid="commercial-quote-cta"
            className="inline-flex items-center gap-2 bg-rust text-cream border border-charcoal/20 shadow-md hover:-translate-y-1 transition-all font-heading text-2xl uppercase px-8 py-4"
          >
            Request a Commercial Quote <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>
    </>
  );
}
